import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Box,
  Button,
  Typography,
  Container,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import signinImage from "../../assets/img/abanotes3.png";
import { useNavigate } from "react-router-dom";
import useSharedLink from "../../SharedLink";
import axiosInstance from "../../api/axiosInstance";
import { BASE_API_URL } from "../../constants";

const PasswordResetOptions = () => {
  const [step, setStep] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [email, setEmail] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate(); 
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const sharedLink = useSharedLink();


  useEffect(() => {
    document.body.style.backgroundImage = `url(${signinImage})`;
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundPosition = "center";

    return () => {
      document.body.style.backgroundImage = "";
      document.body.style.backgroundSize = "";
    };
  }, []);
  const handleVerifyEmail = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${sharedLink}/api/check-email`, { email });

      if (res.data === "Subscriber found") {
        setEmailVerified(true); 
        setSnackbarMessage("Email verified! Click to send verification link.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        setError("Email not registered");
      }
    } catch (err) {
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleSendVerificationLink = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(
      `${BASE_API_URL}/reset-password`,
      { email },
      {
        withCredentials: true,
      });
      setSnackbarMessage("Reset link sent to your email!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage("Failed to send reset link.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    setLoading(false);
  };
  return (
    <Container
    sx={{
      height: "80vh",
      width: { xs: "12", md: "12", xl: "12" },
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backdropFilter: "blur(10px)",
      borderRadius: "10px",
      border: "1px solid rgba(255, 255, 255, 0.18)",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
    }}
    >
      <Box
            sx={{
              height: "50vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "left",
            }}
          >
            <Typography
              className="login-message"
              variant="h2"
              gutterBottom
              sx={{ ml: 10 }}
            >
              Reset
            </Typography>
    
            <Typography
              className="login-message"
              variant="h2"
              gutterBottom
              sx={{
                ml: 10,
              }}
            >
             Your Password
            </Typography>
          </Box>


        <Box
          className="login-container"
        component="form"
        sx={{
          mt: 4,
          mb: 4,
          p: 4,
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          marginLeft: "auto",
          marginRight: "20px",
          width: "400px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
        >
          <Avatar sx={{ m: "auto", bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Reset Your Password
          </Typography>

          {step === 1 && (
            <>
              <Typography sx={{ mb: 3 }}>
                How would you like to reset your password?
              </Typography>

              <Button
                variant="contained"
                sx={{
                  mb: 2,
                  width: "100%",
                  bgcolor: "#e9a132",
                  "&:hover": { bgcolor: "#d8902e" },
                }}
                onClick={() => setStep(2)}
              >
                Via Verification Link
              </Button>

              <Button
               variant="contained"
               color="secondary"
               sx={{
                 width: "100%",
                 fontFamily: "'Poppins', sans-serif",
                 fontWeight: "500",
                 transition: "all 0.3s ease-in-out",
                 "&:hover": {
                   transform: "scale(1.05)",
                 },
               }}
               onClick={() => navigate("/forgot-password")}
              >
                Answer Security Questions
              </Button>
              <Typography variant="body2" sx={{ mt: 2 }}>
      <Button 
        onClick={() => navigate("/login")} 
        sx={{ textTransform: "none", color: "primary.main", fontSize: "0.875rem" }}
      >
        Back to Sign In
      </Button>
    </Typography>
            </>
          )}

          {step === 2 && (
                <>
                  <Typography
                    sx={{
                      mb: 2,
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: "500",
                    }}
                  >
                    Enter your email to verify
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    maxLength="20"
                    variant="outlined"
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                      {error}
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    sx={{
                      width: "100%",
                      bgcolor: emailVerified ? "#e9a132" : "primary.main",
                      "&:hover": { bgcolor: emailVerified ? "#d8902e" : "primary.dark" },
                    }}
                    onClick={emailVerified ? handleSendVerificationLink : handleVerifyEmail}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : emailVerified ? "Send Verification Link" : "Verify Email"}
                  </Button>
                  <Typography variant="body2" sx={{ mt: 2 }}>
      <Button 
        onClick={() => navigate("/login")} 
        sx={{ textTransform: "none", color: "primary.main", fontSize: "0.875rem" }}
      >
        Back to Sign In
      </Button>
    </Typography>
                </>
                
              )}


        </Box>
  

        <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            onExited={() => {
              setSnackbarMessage("");  
              setSnackbarSeverity("success"); 
            }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert 
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              variant="filled"
            >
              {snackbarMessage}
            </Alert>
        </Snackbar>
    </Container>
  );
};

export default PasswordResetOptions;
